import { useState, useRef, useEffect, useCallback } from "react";

// Reusable text-to-speech playback hook.
// endpoint: full URL to POST { text } and receive audio/mpeg bytes.
export const useTts = (endpoint, authHeader = null) => {
  const [playingKey, setPlayingKey] = useState(null);
  const [loadingKey, setLoadingKey] = useState(null);
  const audioRef = useRef(null);
  const cacheRef = useRef({});

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingKey(null);
  }, []);

  const toggle = useCallback(
    async (key, text) => {
      if (playingKey === key) {
        stop();
        return;
      }
      stop();
      setLoadingKey(key);
      try {
        let url = cacheRef.current[key];
        if (!url) {
          const res = await fetch(endpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json", ...(authHeader || {}) },
            body: JSON.stringify({ text }),
          });
          if (!res.ok) throw new Error("tts failed");
          const blob = await res.blob();
          url = URL.createObjectURL(blob);
          cacheRef.current[key] = url;
        }
        const audio = new Audio(url);
        audioRef.current = audio;
        audio.onended = () => setPlayingKey(null);
        await audio.play();
        setPlayingKey(key);
      } catch (e) {
        setPlayingKey(null);
      } finally {
        setLoadingKey(null);
      }
    },
    [endpoint, authHeader, playingKey, stop]
  );

  useEffect(() => {
    return () => {
      const cache = cacheRef.current;
      if (audioRef.current) audioRef.current.pause();
      Object.values(cache).forEach((u) => URL.revokeObjectURL(u));
    };
  }, []);

  return { playingKey, loadingKey, toggle, stop };
};
